import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import some from 'lodash/some';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import { useCallback } from 'react';
import every from 'lodash/every';

import { formatDoBToAge, formatName } from '@jerry-serverless/tools/lib/formatters';
import { parseFullName } from '@jerry-serverless/name-parser';
import { UpdatePolicyInfoSteps } from '@jerry-serverless/enums/signup';

import type { AppData } from '../types/app_data';
import type { SearchPolicyDriver } from '../types/policy';
import { RelationToUser, MaritalStatus } from '../types/driver';
import { ExcludedReason } from '../types/exclude_reason.enum';
import { DriverRatedTypes } from '../types/rated.enum';
import { useQueryDriverRisks } from '../apollo/graphql/driver_violations.hook';
import type { StepManagerEffectReturnType } from '../steps/types/step_manager';

import { mergeViewerQueryDataInAppData } from './app_data';
import { useConfirmCarsWithMergedAppData } from './car_hook';

/**
 * Filters the `selectedDrivers` to identify drivers with changes in 'name' or 'dateOfBirth'
 * or those who have no recorded violations in both `appData.driverViolations` and their own `violations` property.
 *
 * @param selectedDrivers - The list of drivers selected for filtering.
 * @param appData - Application data containing drivers and violations information.
 * @returns An array of filtered drivers based on the specified criteria.
 */
export const filterUnconfirmedViolationDrivers = (
  selectedDrivers: SearchPolicyDriver[],
  appData: AppData
): SearchPolicyDriver[] => {
  return filter(selectedDrivers, (item) => {
    const driver = find(appData.drivers, { id: item.id });
    const existDependenciesChanged = some(['name', 'dateOfBirth'], (field) => get(item, field) !== get(driver, field));
    return existDependenciesChanged || (!has(appData.driverViolations, item.id) && !driver?.violations?.length);
  });
};

/**
 * Generate a string of driver name and age for the given driver. This can be used as a unique key for the driver
 * because we don't allow drivers with the same name and age.
 */
export const getDriverNameAgeKey = (driver: SearchPolicyDriver) => {
  const age = driver.age || (driver.dateOfBirth ? formatDoBToAge(driver.dateOfBirth) : null);
  let name = formatName(driver.name);

  try {
    if (driver.additionalInfo) {
      const firstName = get(driver.additionalInfo, 'firstName');
      const lastName = get(driver.additionalInfo, 'lastName');
      if (firstName && lastName) {
        name = formatName(`${firstName} ${lastName}`);
      }
    } else {
      const { first: firstName, last: lastName } = parseFullName(driver.name || '', 'all', -1, 0, 1);
      if (firstName && lastName) {
        name = formatName(`${firstName} ${lastName}`);
      }
    }
  } catch {
    // Use the original name if parsing fails
  }

  return `${name}_${age}`;
};

/**
 * Mapping rated with relationshipToUser and excludedReason
 */
export const getHouseholdMemberRatedValue = (
  relationshipToUser?: RelationToUser | null,
  excludedReason?: ExcludedReason | null
): DriverRatedTypes => {
  switch (relationshipToUser) {
    case RelationToUser.Spouse:
    case RelationToUser.DomesticPartner:
    case RelationToUser.Relative:
    case RelationToUser.Child:
    case RelationToUser.Roommate:
      return excludedReason === ExcludedReason.NeverLicensed ? DriverRatedTypes.NonRated : DriverRatedTypes.Excluded;
    case RelationToUser.Other:
      return excludedReason === ExcludedReason.MilitaryDeployedOverseas
        ? DriverRatedTypes.NonRated
        : DriverRatedTypes.Excluded;
    default:
      return DriverRatedTypes.Excluded;
  }
};

export const isRated = (driver: SearchPolicyDriver): boolean =>
  driver.rated === DriverRatedTypes.Rated || isNil(driver.rated);

/**
 * Get initial policy driver values.
 * @param appData AppData
 * @returns Searched policy drivers
 */
export const getDrivers: (appData: AppData) => SearchPolicyDriver[] | undefined = (appData) => {
  // If tempDrivers exists, it should have the latest changes
  if (appData.tempDrivers && appData.tempDrivers.length > 0) {
    return appData.tempDrivers;
  }
  // If drivers data hasn't been sync'ed, use the info pulled from `searchPolicyInfo` query.
  // NOTE: We'll NOT use the marital status from the pulled data.
  if (!appData.syncedPolicyDrivers && size(appData.searchPolicyData?.drivers) > 0) {
    return appData.searchPolicyData?.drivers?.map((driver) => ({
      ...driver,
      maritalStatus: driver.relationToUser === RelationToUser.Insured ? null : MaritalStatus.Single,
      relationToUser: driver.relationToUser === RelationToUser.Insured ? driver.relationToUser : null,
    }));
  }

  // If it has been sync'ed, assemble data from user's drivers.
  // NOTE: For drivers, the user's driver needs to be at the front.
  return sortBy(
    filter(appData.drivers || [], (drv) => !drv.isArchived),
    (drv) => drv.id !== appData.driverID
  );
};

export const useAddSpouseAndGoNext = (
  appData: AppData
): {
  addSpouseAndGoNext: (options: {
    newDriver: SearchPolicyDriver;
    goNext: StepManagerEffectReturnType['goNext'];
  }) => void;
  isAddingSpouse: boolean;
  addingSpouseError: string | undefined;
} => {
  const { confirmCarsWithMergedAppData, updating, error } = useConfirmCarsWithMergedAppData(
    appData,
    appData.selectedCars || [],
    undefined,
    UpdatePolicyInfoSteps.Drivers
  );

  const [isLoading, queryDriverViolations] = useQueryDriverRisks();

  const addSpouseAndGoNext = useCallback(
    async (options: { newDriver: SearchPolicyDriver; goNext: StepManagerEffectReturnType['goNext'] }) => {
      const { newDriver, goNext } = options;
      const tempDrivers = appData.tempDrivers?.concat({ ...newDriver, selected: true, userAdded: true });
      const selectedDrivers = filter(tempDrivers, (drv) => !!drv.selected);

      let driverViolations = appData.driverViolations || {};
      if (appData.signupParams?.violations) {
        driverViolations = await queryDriverViolations(filterUnconfirmedViolationDrivers(selectedDrivers, appData));
      }
      const mergedAppData: AppData = {
        ...appData,
        addingNewDriver: undefined,
        drivers: selectedDrivers,
        tempDrivers,
        driverViolations,
        syncedDriverViolations: appData.syncedDriverViolations && every(driverViolations, (v) => !v.length),
        shouldSelectPartner: false,
      };

      confirmCarsWithMergedAppData(mergedAppData, async (user) => {
        void goNext(
          mergeViewerQueryDataInAppData(user || null, {
            ...mergedAppData,
            syncedPolicyData: true,
          }),
          'replace'
        );
      });
    },
    [appData, confirmCarsWithMergedAppData, queryDriverViolations]
  );

  return {
    addSpouseAndGoNext,
    isAddingSpouse: updating || isLoading,
    addingSpouseError: error,
  };
};

/**
 * List of spouse candidates to display to let user choose from
 * 1. The drivers which are selected and not assigned a relationship to user
 * 2. The newly added/chosen spouse driver, ensuring that upon returning from a previous step, users can still see their chosen/added spouse
 */
export const useGetPartnerCandidates = () => {
  return useCallback((drivers: SearchPolicyDriver[]) => {
    return filter(
      drivers,
      (drv) =>
        drv.selected &&
        (isNil(drv.relationToUser) ||
          drv.relationToUser === RelationToUser.Spouse ||
          drv.relationToUser === RelationToUser.DomesticPartner)
    );
  }, []);
};
